import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Printer, Stethoscope } from "lucide-react";
import { fmtDate, inr } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicBill() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/share/bill/${token}`).then((r) => setData(r.data)).catch(() => setErr("Not found"));
  }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-[#8A9992]">Bill not found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#8A9992]">Loading…</div>;

  const { bill: b, doctor: d, patient: p } = data;

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="no-print flex justify-end mb-4">
          <button onClick={() => window.print()} className="btn-emerald"><Printer size={16} /> Print / Save PDF</button>
        </div>
        <div className="card-soft p-10">
          <header className="flex items-start justify-between pb-6 border-b-2 border-[#0A4C36]">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-2xl bg-[#0A4C36] text-white flex items-center justify-center"><Stethoscope size={20} /></div>
                <div>
                  <div className="text-2xl font-semibold text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>Dr. {d?.name?.replace(/^Dr\.?\s*/i, "")}</div>
                  <div className="text-xs text-[#6C7B72]">{d?.qualification}</div>
                </div>
              </div>
              {d?.clinic_name && <div className="text-sm font-semibold text-[#1F2A24]">{d.clinic_name}</div>}
              <div className="text-xs text-[#6C7B72] whitespace-pre-line">{d?.address}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-widest uppercase text-[#8A9992]">Invoice</div>
              <div className="text-xs text-[#1F2A24] mt-1">{fmtDate(b?.created_at)}</div>
              <div className="text-[11px] text-[#8A9992] mt-1">ID: {b?.id?.slice(0, 8)}</div>
            </div>
          </header>

          <section className="grid grid-cols-2 gap-6 py-6 border-b border-[#EFECE5]">
            <Info label="Billed to" value={p?.name} />
            <Info label="Mobile" value={p?.mobile} />
          </section>

          <section className="py-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] tracking-widest uppercase text-[#8A9992]">
                  <th className="pb-3">Item</th>
                  <th className="pb-3 text-right">Qty</th>
                  <th className="pb-3 text-right">Price</th>
                  <th className="pb-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {b?.items?.map((it, i) => (
                  <tr key={i} className="border-t border-[#EFECE5]">
                    <td className="py-3 text-[#1F2A24]">{it.label}</td>
                    <td className="py-3 text-right">{it.quantity}</td>
                    <td className="py-3 text-right">{inr(it.unit_price)}</td>
                    <td className="py-3 text-right font-medium">{inr(it.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="pt-4 border-t border-[#EFECE5] text-sm">
            <div className="flex justify-between py-1"><span className="text-[#6C7B72]">Subtotal</span><span>{inr(b?.subtotal)}</span></div>
            <div className="flex justify-between py-1"><span className="text-[#6C7B72]">Tax</span><span>{inr(b?.tax)}</span></div>
            {b?.discount ? <div className="flex justify-between py-1"><span className="text-[#6C7B72]">Discount</span><span>- {inr(b.discount)}</span></div> : null}
            <div className="flex justify-between py-2 mt-2 border-t border-[#0A4C36]">
              <span className="font-semibold">Total</span>
              <span className="text-xl font-semibold text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>{inr(b?.total)}</span>
            </div>
          </section>

          {b?.notes && <section className="pt-4 text-xs text-[#6C7B72]">Notes: {b.notes}</section>}

          <footer className="pt-8 mt-6 border-t border-[#EFECE5] text-center text-xs text-[#6C7B72]">
            Thank you for your visit. Wishing you a speedy recovery.
          </footer>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest uppercase text-[#8A9992]">{label}</div>
      <div className="text-sm text-[#1F2A24] mt-1">{value || "—"}</div>
    </div>
  );
}
